import { trackError } from '@iheartjane/tracking';
import type { ReservationMode } from '@iheartjane/types';
import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import type { DeepReadonly } from 'ts-essentials';

// eslint-disable-next-line no-restricted-imports
import type { Location, MedRec, Store } from '@jane/shared/models';

import type { ShopByMode } from '../../operatorEmbed/components/operatorEmbedGates';
import { OperatorStoresSource } from '../../operatorEmbed/sources/operatorStoresSource';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';
import { setPartner, verifyStore } from './embeddedApp';
import { getStoreError, initiateGetStore } from './store';

const SET_OPERATOR = 'operatorEmbedded/setOperator';
export const setOperator = createAction<
  {
    id: OperatorEmbeddedState['operatorId'];
    name: OperatorEmbeddedState['operatorName'];
  },
  typeof SET_OPERATOR
>(SET_OPERATOR);

const SET_RESERVATION_MODE = 'operatorEmbedded/setReservationMode';
export const setReservationMode = createAction<
  OperatorEmbeddedState['reservationMode'] | null,
  typeof SET_RESERVATION_MODE
>(SET_RESERVATION_MODE);

export const setStore =
  ({
    operator,
    mode,
    userLocation,
    medOrRec,
    productId,
  }: {
    medOrRec: MedRec;
    mode: ShopByMode;
    operator: { id: string | number };
    productId: number;
    userLocation: Location;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateGetStore());
    const compactedParams = omitBy(
      {
        reservation_mode: mode,
        lat: userLocation?.coordinates?.lat,
        long: userLocation?.coordinates?.long,
        med_rec: medOrRec,
        product_id: productId,
      },
      isNil
    );
    OperatorStoresSource.get(operator.id, compactedParams)
      .then(({ store }: { store: Store }) => {
        if (store) {
          dispatch(
            setPartner({
              id: store.id,
              name: store.name,
            })
          );

          dispatch(
            verifyStore({
              storeId: store.id,
              options: { operatorEmbed: true },
            })
          );
        } else {
          dispatch(getStoreError(null));
        }
      })
      .catch((e) => {
        trackError(e, {
          params: compactedParams,
        });
      });
  };

export type OperatorEmbeddedActions =
  | ReturnType<typeof setOperator>
  | ReturnType<typeof setReservationMode>;

type OperatorEmbeddedState = DeepReadonly<{
  operatorId: number | string | undefined;
  operatorName: string | undefined;
  reservationMode: ReservationMode | undefined;
}>;

const getInitialState = (): OperatorEmbeddedState => ({
  operatorId: undefined,
  operatorName: undefined,
  reservationMode: undefined,
});

export const operatorEmbeddedReducer: Reducer<
  OperatorEmbeddedState,
  CustomerAction
> = (state = getInitialState(), action) => {
  switch (action.type) {
    case SET_OPERATOR:
      return {
        ...state,
        operatorId: action.payload.id,
        operatorName: action.payload.name,
      };
    case SET_RESERVATION_MODE:
      return { ...state, reservationMode: action.payload || undefined };
  }

  return state;
};

export const selectOperatorEmbedded = (state: AppState) =>
  state.operatorEmbedded;
